import { useEffect, useState } from "react";
import { Loader2, Send, X } from "lucide-react";
import { getBackendUrl, authHeaders } from "../lib/http";
import { useShopStore } from "../store/shop.store";

/** Urun adi "English / Türkçe": Etsy'de yalniz Ingilizce kisim aranir */
export const defaultKeyword = (name: string) => name.split("/")[0].trim();

async function createInspirationTask(payload: { catalogId: string; categoryId: string; productName: string; keyword: string; shopId: string | null }) {
  const r = await fetch(`${getBackendUrl()}/api/tasks`, {
    method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ type: "inspiration", payload }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error ?? `Görev oluşturulamadı (${r.status})`);
}

// Takı: katalog ürünü için eklentiye "ilham listesi bul" görevi verir (API: /api/tasks type=inspiration).
// Eklenti yan panel > Keşif > İlham görevleri'nde açılır; kullanıcı Etsy'de seçip ilham listesine ekler.
export default function InspirationTaskButton({ catalogId, categoryId, productName }: { catalogId: string; categoryId: string; productName: string }) {
  const shopId = useShopStore((s) => s.activeShopId);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState(defaultKeyword(productName));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const send = async () => {
    setBusy(true); setMsg(null);
    try {
      await createInspirationTask({ catalogId, categoryId, productName, keyword: keyword.trim(), shopId });
      setMsg({ ok: true, text: "Görev verildi. Eklentide Keşif sekmesindeki “İlham görevleri”nde açıldı." });
      setOpen(false);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-sm">
      {!open ? (
        <button type="button" onClick={() => { setOpen(true); setMsg(null); }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
          <Send className="h-3.5 w-3.5" /> Eklentiye ilham görevi ver
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && keyword.trim()) void send(); }}
            aria-label="Etsy'de aranacak anahtar kelime" placeholder="Etsy'de aranacak anahtar kelime"
            className="min-w-56 flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300" />
          <button type="button" onClick={send} disabled={busy || !keyword.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Görevi ver
          </button>
          <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100">Vazgeç</button>
        </div>
      )}
      {msg && <p className={`mt-1 text-xs ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.text}</p>}
    </div>
  );
}

/** Toplu ilham gorevi: listedeki urunler icin tek seferde gorev. Varsayilan
 *  secim: ilham listesi olmayan ve acik (bekleyen/calisan) gorevi olmayanlar. */
export function BulkInspirationModal({ catalogId, products, onClose }: {
  catalogId: string;
  products: Array<{ id: string; name: string; hasInspiration: boolean }>;
  onClose: () => void;
}) {
  const shopId = useShopStore((s) => s.activeShopId);
  const [openTask, setOpenTask] = useState<Set<string> | null>(null);
  const [rows, setRows] = useState(() => products.map((p) => ({ ...p, keyword: defaultKeyword(p.name), on: !p.hasInspiration })));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Record<string, "ok" | string>>({});

  // Acik ilham gorevi olan urunler: tekrar gorev verilmez (varsayilan secimden cikar)
  useEffect(() => {
    void fetch(`${getBackendUrl()}/api/tasks?type=inspiration&open=1`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { tasks: [] }))
      .then((j: { tasks?: Array<{ payload?: { catalogId?: string; categoryId?: string } }> }) => {
        const ids = new Set((j.tasks ?? []).filter((t) => t.payload?.catalogId === catalogId).map((t) => String(t.payload?.categoryId)));
        setOpenTask(ids);
        setRows((cur) => cur.map((r) => (ids.has(r.id) ? { ...r, on: false } : r)));
      })
      .catch(() => setOpenTask(new Set()));
  }, [catalogId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const picked = rows.filter((r) => r.on && r.keyword.trim() && done[r.id] !== "ok");
  async function sendAll() {
    setBusy(true);
    for (const r of picked) {
      try {
        await createInspirationTask({ catalogId, categoryId: r.id, productName: r.name, keyword: r.keyword.trim(), shopId });
        setDone((d) => ({ ...d, [r.id]: "ok" }));
      } catch (e) {
        setDone((d) => ({ ...d, [r.id]: e instanceof Error ? e.message : String(e) }));
      }
    }
    setBusy(false);
  }
  const okCount = Object.values(done).filter((v) => v === "ok").length;
  const setAll = (on: boolean) => setRows((cur) => cur.map((r) => ({ ...r, on: on && done[r.id] !== "ok" })));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && onClose()}>
      <div role="dialog" aria-modal="true" aria-label="Toplu ilham görevi" className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Send className="h-4 w-4 text-indigo-600" />Toplu ilham görevi</h3>
          <button onClick={onClose} disabled={busy} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-2 text-xs text-gray-600">
          <span>{rows.filter((r) => r.on).length} / {rows.length} seçili</span>
          <button onClick={() => setAll(true)} disabled={busy} className="text-indigo-700 hover:underline">Tümünü seç</button>
          <button onClick={() => setAll(false)} disabled={busy} className="text-indigo-700 hover:underline">Hiçbiri</button>
          <span className="ml-auto text-gray-500">Varsayılan: ilham listesi ve açık görevi olmayanlar</span>
        </div>
        <ul className="flex-1 divide-y divide-gray-100 overflow-y-auto px-5">
          {rows.map((r, i) => {
            const st = done[r.id];
            return (
              <li key={r.id} className="flex items-center gap-3 py-2">
                <input type="checkbox" checked={r.on} disabled={busy || st === "ok"} aria-label={`${r.name} seç`}
                  onChange={(e) => setRows((cur) => cur.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)))}
                  className="h-4 w-4 accent-indigo-600" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-gray-500" title={r.name}>
                    {r.name}
                    {r.hasInspiration && <span className="ml-1.5 rounded-full bg-emerald-50 px-1.5 text-emerald-700">ilham var</span>}
                    {openTask?.has(r.id) && <span className="ml-1.5 rounded-full bg-amber-50 px-1.5 text-amber-700">açık görev</span>}
                  </p>
                  <input value={r.keyword} disabled={busy || st === "ok"} aria-label="Anahtar kelime"
                    onChange={(e) => setRows((cur) => cur.map((x, j) => (j === i ? { ...x, keyword: e.target.value } : x)))}
                    className="mt-0.5 w-full rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-50" />
                  {st && st !== "ok" && <p className="mt-0.5 text-xs text-red-600">{st}</p>}
                </div>
                {st === "ok" && <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Verildi</span>}
              </li>
            );
          })}
        </ul>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          {okCount > 0 && <p className="mr-auto text-sm text-emerald-700">{okCount} görev verildi — eklentide Keşif › İlham görevleri.</p>}
          <button onClick={onClose} disabled={busy} className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50">Kapat</button>
          <button onClick={() => void sendAll()} disabled={busy || picked.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {picked.length} ürüne görev ver
          </button>
        </div>
      </div>
    </div>
  );
}
